import { securitySchemes } from './generated/contract.ts';
import type {
  CredentialHeaderName,
  CredentialPlacement,
  OperationDescriptor,
  OperationId,
} from './operation.ts';

/**
 * Which credential an operation gets, decided from the operation's own
 * `security` rather than from whatever the caller happens to be holding.
 *
 * This API authenticates two different things. `session` is a person signed in
 * at the back-office; `terminalLease` is a registered device, and the document
 * says in as many words that a session must never be sent as a lease nor a
 * lease as a bearer token. A terminal holds both at once — the device has a
 * lease and a supervisor signs in on top of it — so "attach whatever we have"
 * is not a simplification, it is the swap the document warns about.
 */

/** The scheme names the document publishes: `session` and `terminalLease`. */
export type SecuritySchemeName = keyof typeof securitySchemes;

/**
 * Resolved per request, so a session issued after sign-in or a lease that
 * renewed mid-shift is picked up without rebuilding the client. Returning
 * `undefined` means "not held yet" and sends nothing.
 */
export type CredentialProvider = () => string | undefined | Promise<string | undefined>;

/** One provider per scheme. Supply only the credentials this client can hold. */
export type CredentialSource = Partial<Record<SecuritySchemeName, CredentialProvider>>;

/**
 * Every header name a selected credential can occupy, lower-cased: today
 * `authorization` and `x-terminal-lease`.
 *
 * The transport strips these from the caller's own headers, so a credential
 * enters from `credentials` and nowhere else. Merging is not enough on its own:
 * the selected credential wins by *name*, so it displaces a parked one only
 * where the two share a header — and these two schemes deliberately do not. A
 * lease parked in `headers` therefore reached every back-office route and the
 * unauthenticated sign-in.
 *
 * Derived from the document's `securitySchemes` rather than listed here, so a
 * scheme added upstream is covered by regenerating and nothing else.
 */
export const credentialHeaderNames: ReadonlySet<string> = new Set(
  Object.values(securitySchemes).map((placement: CredentialPlacement) =>
    placement.kind === 'bearer' ? 'authorization' : placement.header.toLowerCase(),
  ),
);

/**
 * The same names again, at the type level, checked against the document.
 *
 * `OperationHeaders` must exclude every credential header, or a route that
 * declares its own credential as a parameter — three terminal reads do —
 * compiles into a call site obliged to pass a lease by hand, which the
 * transport then strips. The union lives in `operation.ts` because
 * `contract.ts` imports from there and deriving it would close a cycle; this
 * is what keeps the two from drifting. A scheme added upstream with a new
 * header fails here, at `pnpm typecheck`, rather than at a till.
 */
export type PublishedCredentialHeader = Lowercase<
  Extract<(typeof securitySchemes)[SecuritySchemeName], { kind: 'header' }>['header']
>;

const _everyCredentialHeaderIsExcluded = (name: PublishedCredentialHeader): CredentialHeaderName =>
  name;
void _everyCredentialHeaderIsExcluded;

function placementFor(scheme: string): CredentialPlacement {
  // Own rows only. A scheme named after an `Object.prototype` member resolves
  // up the prototype chain rather than missing, which skips the refusal below
  // and sends the credential under a header literally named `undefined`. The
  // document is fetched over the network at generate time, so its scheme names
  // are not this client's to trust.
  const placement: CredentialPlacement | undefined = Object.hasOwn(securitySchemes, scheme)
    ? (securitySchemes as Record<string, CredentialPlacement | undefined>)[scheme]
    : undefined;

  if (!placement) {
    // The operation names a scheme the document's securitySchemes block does
    // not define. Guessing a placement would put a credential somewhere the API
    // never asked for it, so refuse rather than send it anywhere.
    throw new Error(
      `Operation security names "${scheme}", which the OpenAPI document does not define as a ` +
        'security scheme. Regenerate the client; if the document really says this, report it ' +
        'against blossom-rest-api rather than guessing where the credential goes.',
    );
  }

  return placement;
}

const headerFor = (placement: CredentialPlacement, value: string): Record<string, string> =>
  placement.kind === 'bearer'
    ? { authorization: `Bearer ${value}` }
    : { [placement.header]: value };

/**
 * The credential headers for one call, or `{}` when the operation is public or
 * the credential it wants is not held yet.
 *
 * Not holding it is not an error: sending nothing lets the API answer its own
 * 401 rather than having the client invent one, and `Authorization: Bearer `
 * with nothing after it is a malformed credential rather than an absent one.
 *
 * `security` is a list of *alternatives* — the first whose every scheme
 * resolves wins, in document order. Every operation in this document names
 * exactly one, so the loop exists to be right rather than to be exercised.
 */
export async function credentialHeadersFor<Id extends OperationId>(
  operation: OperationDescriptor<Id>,
  credentials: CredentialSource | undefined,
): Promise<Record<string, string>> {
  for (const requirement of operation.security) {
    const schemes = Object.keys(requirement);
    if (schemes.length === 0) continue;

    const headers: Record<string, string> = {};
    let satisfied = true;

    for (const scheme of schemes) {
      // Resolved before the credential is: a scheme with no placement is a
      // broken contract whether or not this client happens to hold its
      // credential, and finding out only when it does is finding out on a till.
      const placement = placementFor(scheme);

      // Own rows here too. `credentials` is a caller-supplied plain object and
      // `scheme` comes from the document; unreachable while `placementFor`
      // gates first, but this is the third lookup of this shape and it costs a
      // line to keep it from becoming reachable when that ordering changes.
      const provider =
        credentials && Object.hasOwn(credentials, scheme)
          ? credentials[scheme as SecuritySchemeName]
          : undefined;
      const value = await provider?.();

      if (!value) {
        satisfied = false;
        break;
      }

      Object.assign(headers, headerFor(placement, value));
    }

    if (satisfied) return headers;
  }

  return {};
}
